import { FluidMenuItem, FluidOption } from '@lmig/fluid-core';

import { Utils } from '../../../utils/utils';
import { FluidI18nState, FluidI18nStore } from '../store/fluid-i18n.store';

/**
 * Short-hand function for updating a component state with
 * the current language.
 */
export function setI18nState() {
  this._i18nBound = true;
  this._lang = this._i18nStore?.getLanguage();
}

/**
 * Find the root i18n provider, register the element with its store,
 * and set the store on the local element to aid quicker lookups.
 */
export function useI18n(
  callbacks?: Record<keyof FluidI18nState, (...args) => void>
) {
  this._i18nProvider = findRootProvider<HTMLFluidI18nProviderElement>(
    'fluid-i18n-provider'
  ) as HTMLFluidI18nProviderElement;
  this._translateId = `${this.host.tagName}_${this.host.id}`;

  // If no i18n provider, but component has init callback, call
  // it anyway in case of state updates required.
  if (Utils.isNullOrUndefined(this._i18nProvider) && callbacks?.['init']) {
    callbacks['init']();
  } else {
    // Otherwise, apply callbacks.
    this._i18nProvider?.getStore().then((store: FluidI18nStore) => {
      if (!this._i18nStore) {
        this._i18nStore = store;
        applyCallbacks.bind(this)(callbacks, store);
      }
    });
  }
}

export function hasTranslationAtKey<T>(
  fluidTranslate: T,
  i18nStore: FluidI18nStore,
  key: keyof T
) {
  return !!(
    fluidTranslate &&
    fluidTranslate[key] &&
    i18nStore?.translate(fluidTranslate[key] as unknown as string)
  );
}

/**
 * Given a list of callbacks for store keys, apply each to the
 * given store when the store key value changes.
 * @param callbacks
 * @param store
 */
function applyCallbacks(
  callbacks: Record<keyof FluidI18nState | 'init', (...args) => void>,
  store: FluidI18nStore
) {
  Object.keys(callbacks || {}).forEach(
    (storeProp: keyof FluidI18nState | 'init') => {
      if (storeProp === 'init') {
        callbacks['init']();
      } else {
        store.onValueChange(storeProp, callbacks[storeProp]);
      }
    }
  );
}

/**
 * Translates an array of FluidMenuItems using the given i18n store.
 * @param menuToTranslate
 * @param store
 */
export function translateMenu(
  menuToTranslate: FluidMenuItem[],
  store: FluidI18nStore
) {
  if (!menuToTranslate) return;
  const mapMenu = (menu: FluidMenuItem[]) =>
    menu.map((item: FluidMenuItem) => {
      if (item.submenu) {
        item.submenu = mapMenu(item.submenu);
      }
      item.label = store?.translate(item.fluidTranslate?.label) || item.label;
      return item;
    });

  return mapMenu(menuToTranslate);
}

/**
 * Translate an array of FluidOptions using the given i18n store.
 * @param options - the options to translate
 * @param store - the translation store to translate values
 * @param valueTranslationMap - a value -> translationKey map if using value-based translation strategy
 */
export type TranslatableFluidOption = FluidOption & {
  fluidTranslate?: { label?: string };
};

export function translateOption(
  option: TranslatableFluidOption,
  store: FluidI18nStore,
  fluidTranslate: any
): string {
  return (
    (option?.fluidTranslate
      ? store?.translate(option?.fluidTranslate?.label)
      : store?.translate(fluidTranslate?.options?.[option.value])) ||
    option.label
  );
}

export function translateOptions(
  options: TranslatableFluidOption[],
  store: FluidI18nStore,
  valueTranslationMap: { [key: string]: string } = {}
) {
  if (!options) return;

  if (options.some((option) => option.fluidTranslate)) {
    return useOptionStrategy(options, store);
  } else {
    return useValueStrategy(options, store, valueTranslationMap);
  }
}

/**
 * Translates options based on the translation keys being configured at an option level (i.e, in the FluidOption
 * definition)
 * @param options
 * @param store
 */
function useOptionStrategy(
  options: TranslatableFluidOption[],
  store: FluidI18nStore
): FluidOption[] {
  return options.map(
    (option) =>
      ({
        ...option,
        label: store?.translate(option.fluidTranslate?.label) || option.label,
      } as FluidOption)
  );
}

/**
 * Translates options based on the translation keys being configured as a value map at the component level.
 * @param options
 * @param store
 * @param valueTranslationMap
 */
function useValueStrategy(
  options: TranslatableFluidOption[],
  store: FluidI18nStore,
  valueTranslationMap?: { [key: string]: string }
): FluidOption[] {
  return options.map(
    (option) =>
      ({
        ...option,
        label:
          store?.translate(valueTranslationMap[option.value]) || option.label,
      } as FluidOption)
  );
}

/**
 * Shorthand function to translate a property that exists on both the
 * fluidTranslate property and the callee. If translation exists, use it,
 * otherwise, use the callee value.
 *
 * @usage translateProperty.bind(this)('propertyName');
 * @param property
 */
export function translateProperty(property: string) {
  return (
    this._i18nStore?.translate(this.fluidTranslate?.[property]) ||
    this[property]
  );
}

/**
 * Translates any key from the  store, returning the fallback
 * value if no translation exists, or no store is provided.
 * @param store
 * @param translationKey
 * @param fallback
 */
export function translateWithFallback(
  store: FluidI18nStore,
  translationKey: string,
  fallback: string
) {
  return store?.translate(translationKey) || fallback;
}

export function findRootProvider<T>(providerSelector: string): T | null {
  let currentWindow: Window & typeof globalThis = window;
  let highestInstance: T | null = null;

  const findElement = () => {
    const elements = currentWindow.document.querySelectorAll(providerSelector);
    if (elements.length > 0) {
      highestInstance = elements[0] as unknown as T;
    }
  };

  if (currentWindow === window.top) {
    findElement();
  }

  while (currentWindow !== window.top) {
    findElement();
    currentWindow = currentWindow.parent as Window & typeof globalThis;
  }

  return highestInstance as unknown as T;
}
